import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'remeda'
import { useEventListener } from 'usehooks-ts'
import { addToCartStatusChangeMilliseconds } from '@velasca/constants'
import { useCart } from '@velasca/hooks/use-cart'
import { handleError } from '@velasca/utils/handle-error'
import * as S from './QuickAddDropdown.styles'
import type { QuickAddDropdownProps } from './QuickAddDropdown.types'

export const QuickAddDropdown = ({
  sizes,
  handle,
  onAddToCart,
  shouldOpenCartDrawerOnAddToCart,
  condensedLayout = false,
  stickToAddedToCart = false,
  buttonLabelOverride,
}: QuickAddDropdownProps) => {
  const { t } = useTranslation('product')
  const [isOpen, setIsOpen] = useState(false)
  const [sizesState, setSizesState] = useState(sizes)
  const [quickAddState, setQuickAddState] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle')
  const { addToCart, setIsDrawerOpen } = useCart()

  const dropdownId = useMemo(() => {
    return `quick-add-dropdown-${sizesState
      ?.map((size) => size.id)
      .sort()
      .join('-')}`
  }, [sizesState])

  const decreaseQuantity = useCallback((id: string) => {
    setSizesState((prevSizes) =>
      prevSizes?.map((size) =>
        size.id === id
          ? { ...size, quantityAvailable: size.quantityAvailable - 1 }
          : size,
      ),
    )
  }, [])

  const handleClickOutside = (event: MouseEvent) => {
    if (!isOpen) {
      return
    }
    const target = event.target as Element

    if (!target.closest(`[data-quick-add-dropdown="${dropdownId}"]`)) {
      setIsOpen(false)
    }
  }

  const handleAddToCart = useCallback(
    (id: string) => {
      setQuickAddState('loading')
      addToCart(id, 1)
        .then(() => {
          setQuickAddState('success')
          decreaseQuantity(id)
          setTimeout(() => {
            window.location.pathname !== '/cart' &&
              shouldOpenCartDrawerOnAddToCart &&
              setIsDrawerOpen(true)
          }, addToCartStatusChangeMilliseconds)

          // If not sticky, revert back to idle after the success delay
          if (!stickToAddedToCart) {
            setTimeout(() => {
              setQuickAddState('idle')
            }, addToCartStatusChangeMilliseconds)
          }
        })
        .catch((error) => {
          setQuickAddState('error')
          handleError(error, { message: "Couldn't add product to cart." })

          // Always revert to idle after showing error state
          setTimeout(() => {
            setQuickAddState('idle')
          }, addToCartStatusChangeMilliseconds)
        })
        .finally(() => {
          onAddToCart?.(handle)
        })
    },
    [
      addToCart,
      setIsDrawerOpen,
      decreaseQuantity,
      shouldOpenCartDrawerOnAddToCart,
      onAddToCart,
      handle,
      stickToAddedToCart,
    ],
  )

  useEventListener('mousedown', handleClickOutside)

  // Keep UI in sync with external cart state for sticky success behavior
  const prevStickyRef = useRef(stickToAddedToCart)

  useEffect(() => {
    // When it becomes sticky, ensure we show success (and avoid redundant set if already success)
    if (stickToAddedToCart && quickAddState !== 'success') {
      setQuickAddState('success')
    }

    // When it transitions from sticky -> not sticky, revert success back to idle
    if (
      !stickToAddedToCart &&
      prevStickyRef.current &&
      quickAddState === 'success'
    ) {
      setQuickAddState('idle')
    }

    prevStickyRef.current = stickToAddedToCart
  }, [stickToAddedToCart, quickAddState])

  const buttonLabel = useMemo(() => {
    // Show success message when in success state
    if (quickAddState === 'success') {
      return t('addedToCart')
    }

    // Otherwise, if a label override was provided (e.g., wishlist size), prefer it
    if (buttonLabelOverride) {
      return buttonLabelOverride
    }

    return t('quickAdd')
  }, [buttonLabelOverride, quickAddState, t])

  const icon = useMemo(() => {
    if (quickAddState === 'loading') {
      return <S.IconLoading />
    }
    if (quickAddState === 'success') {
      return <S.IconSuccess />
    }
    if (quickAddState === 'error') {
      return <S.IconError />
    }
    if (sizesState && sizesState.length > 1) {
      return <S.IconCaret />
    }

    return <S.IconEmpty />
  }, [sizesState, quickAddState])

  if (!sizesState || isEmpty(sizesState)) {
    return null
  }

  return (
    <S.Container
      data-quick-add-dropdown={dropdownId}
      condensedLayout={condensedLayout}
    >
      <S.Button
        condensedLayout={condensedLayout}
        disabled={quickAddState !== 'idle'}
        success={quickAddState === 'success'}
        tabIndex={quickAddState !== 'idle' ? -1 : 0}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          if (quickAddState !== 'idle') {
            return
          }
          if (sizesState && sizesState.length > 1) {
            setIsOpen((prev) => !prev)
          } else if (sizesState && sizesState.length === 1) {
            handleAddToCart(sizesState[0].id)
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.stopPropagation()
            if (quickAddState !== 'idle') {
              return
            }
            if (sizesState && sizesState.length > 1) {
              setIsOpen((prev) => !prev)
            } else if (sizesState && sizesState.length === 1) {
              handleAddToCart(sizesState[0].id)
            }
          }
        }}
      >
        {buttonLabel}
        {icon}
      </S.Button>
      {sizesState && !isEmpty(sizesState) && (
        <S.Dropdown
          aria-expanded={isOpen}
          aria-labelledby={dropdownId}
          opened={isOpen}
        >
          {sizesState.map((size) => (
            <S.DropdownItem
              key={size.id}
              isOutOfStock={size.quantityAvailable === 0}
              role="button"
              aria-disabled={size.quantityAvailable === 0}
              tabIndex={size.quantityAvailable > 0 ? 0 : -1}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                if (quickAddState !== 'idle') {
                  return
                }
                setIsOpen(false)
                size.quantityAvailable > 0 && handleAddToCart(size.id)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  if (quickAddState !== 'idle') {
                    return
                  }
                  setIsOpen(false)
                  size.quantityAvailable > 0 && handleAddToCart(size.id)
                }
              }}
            >
              <span>{size.name}</span>
              {size.quantityAvailable === 0 && (
                <span>{t('temporarilyNotAvailable')}</span>
              )}
            </S.DropdownItem>
          ))}
        </S.Dropdown>
      )}
    </S.Container>
  )
}
